import React, { useState, useEffect } from "react";
import { Download, Plus, X, Eye, Edit } from "lucide-react";
import { getPayslips, createPayslip, updatePayslipById } from "../../../src/services/AdminPortal/PayslipAPI";
import AddPayslipForm from "./AddPayslipForm";
import PayslipView from "./PayslipView";

const PayslipManagement = () => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [editPayslip, setEditPayslip] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayslips();
  }, []);

  const fetchPayslips = async () => {
    try {
      const data = await getPayslips();
      setPayslips(data || []);
    } catch (err) {
      console.error("Fetch payslips error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditPayslip(null);
    setShowFormModal(true);
  };

  const handleEdit = (payslip) => {
    setEditPayslip(payslip);
    setShowFormModal(true);
  };

  const handleCreate = async (payslipData) => {
    try {
      if (editPayslip) {
        await updatePayslipById(editPayslip._id, payslipData);
      } else {
        await createPayslip(payslipData);
      }
      setShowFormModal(false);
      setEditPayslip(null);
      await fetchPayslips();
    } catch (error) {
      console.error("Error saving payslip:", error);
    }
  };

  const handleView = (payslip) => {
    setSelectedPayslip(payslip);
    setShowViewModal(true);
  };

  const handleDownload = (payslip) => {
    setSelectedPayslip(payslip);
    setShowViewModal(true);
  };

  return (
    <div className="p-6">
      <div className="bg-[#131313] border border-[#333] p-4 rounded-lg w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-sm font-semibold">
            PAYSLIP MANAGEMENT
          </h2>
          <button
            onClick={handleAddClick}
            className="bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-semibold px-3 py-1 rounded flex items-center gap-1"
          >
            <Plus size={14} /> Add Payslip
          </button>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-8">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] text-gray-200">
              <thead>
                <tr className="text-gray-400 text-[11px] border-b border-[#2a2a2a]">
                  <th className="text-left pb-2">PAYSLIP NO</th>
                  <th className="text-left pb-2">EMPLOYEE</th>
                  <th className="text-center pb-2">MONTH/YEAR</th>
                  <th className="text-right pb-2">TOTAL EARNINGS</th>
                  <th className="text-right pb-2">TOTAL DEDUCTIONS</th>
                  <th className="text-right pb-2">NET SALARY</th>
                  <th className="text-center pb-2">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {payslips.map((ps, idx) => {
                  // compute net pay = earnings - deductions
                  const totalEarnings =
                    (Number(ps.earnings?.basicPay || 0) +
                      Number(ps.earnings?.hra || 0) +
                      Number(ps.earnings?.specialAllowance || 0) +
                      Number(ps.earnings?.incentive || 0));
                  const totalDeductions =
                    (Number(ps.deductions?.pf || 0) +
                      Number(ps.deductions?.professionalTax || 0) +
                      Number(ps.deductions?.incomeTax || 0) +
                      Number(ps.deductions?.other || 0));
                  const netPay = totalEarnings - totalDeductions;

                  return (
                    <tr key={ps._id || idx} className="border-b border-[#2a2a2a]">
                      <td className="py-2">{ps.payslipNo}</td>
                      <td className="py-2">{ps.employeeName}</td>
                      <td className="text-center py-2">{ps.month} {ps.year}</td>
                      <td className="text-right py-2">
                        ₹{Number(totalEarnings || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="text-right py-2">
                        ₹{Number(totalDeductions || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="text-right py-2">
                        ₹{Number(netPay || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="text-center py-2">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleView(ps)}
                            className="text-[#FFCA20] text-xs hover:underline flex items-center gap-1"
                          >
                            <Eye size={12} /> View
                          </button>
                          <button
                            onClick={() => handleEdit(ps)}
                            className="text-blue-400 text-xs hover:underline flex items-center gap-1"
                          >
                            <Edit size={12} /> Edit
                          </button>
                          <button
                            onClick={() => handleDownload(ps)}
                            className="text-gray-400 text-xs hover:underline flex items-center gap-1"
                          >
                            <Download size={12} /> Download
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {payslips.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center text-gray-400 py-8">
                      No payslips found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Payslip Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131313] p-6 rounded-lg w-full max-w-4xl shadow-lg border border-[#333]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-base font-semibold">
                {editPayslip ? "Edit Payslip" : "Add Payslip"}
              </h3>
              <button
                onClick={() => setShowFormModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <AddPayslipForm
              onCancel={() => {
                setShowFormModal(false);
                setEditPayslip(null);
              }}
              onSave={handleCreate}
              editPayslip={editPayslip}
            />
          </div>
        </div>
      )}

      {/* View Payslip Modal */}
      {showViewModal && selectedPayslip && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#131313] p-6 rounded-lg w-full max-w-3xl shadow-lg border border-[#333] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white ml-4 text-lg font-semibold">
                Payslip Preview
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-600 hover:text-black"
              >
                <X size={20} />
              </button>
            </div>

            <PayslipView 
              payslip={selectedPayslip} 
              allowDownload={true} 
              onEdit={fetchPayslips}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PayslipManagement;
